import { useCallback, useState, useMemo } from 'react';
import { IoMdMore } from 'react-icons/io';
import { MdOutlineVolumeOff, MdOutlineInfo, MdOutlinePushPin, MdOutlineVolumeUp } from 'react-icons/md';
import { IconButton } from '@ds';
import { CHANNEL_ACTIONS } from '@features/components/Chat/constants';
import { useChatContext } from 'stream-chat-react';
import { ConditionalRender } from '@ui';

type ChannelMenuProps = {
  setIsDetailsPanelOpen: (open: boolean) => void;
  toggleMuteChannel: () => void;
  togglePinChannel: () => void;
  isPinned: boolean;
  isMuted: boolean;
};

export const ChannelMenu = ({
  setIsDetailsPanelOpen,
  toggleMuteChannel,
  togglePinChannel,
  isPinned,
  isMuted,
}: ChannelMenuProps) => {
  const { channel: activeChannel, client: chatClient } = useChatContext();
  const [showMenu, setShowMenu] = useState(false);

  const canPerformChannelActions = useMemo(() => {
    if (!activeChannel || !chatClient?.userID) return false;
    const isMember = !!activeChannel.state.members?.[chatClient.userID];
    return isMember;
  }, [activeChannel, chatClient.userID]);

  const toggleMenu = useCallback(() => {
    setShowMenu((prev) => !prev);
  }, []);

  const onShowChatDetails = useCallback(() => {
    toggleMenu();
    setIsDetailsPanelOpen(true);
  }, [toggleMenu, setIsDetailsPanelOpen]);

  return (
    <div className="relative">
      <IconButton size="sm" variant="light" startContent={<IoMdMore />} onClick={toggleMenu} />
      {showMenu && (
        <div className="absolute right-0 mt-2 p-1 min-w-[140px] bg-base-90 border border-base-60 rounded-lg shadow-lg z-10">
          <button
            className="flex items-center gap-1 rounded-lg px-1 py-2 text-left text-s-r hover:bg-base-70 w-full"
            onClick={onShowChatDetails}
          >
            <MdOutlineInfo size={16} className="shrink-0" />
            Chat details
          </button>
          <ConditionalRender show={canPerformChannelActions}>
            <button
              className="flex items-center gap-1 rounded-lg px-1 py-2 text-left text-s-r hover:bg-base-70 w-full"
              onClick={toggleMuteChannel}
            >
              {isMuted ? (
                <MdOutlineVolumeUp size={16} className="shrink-0" />
              ) : (
                <MdOutlineVolumeOff size={16} className="shrink-0" />
              )}
              {isMuted ? CHANNEL_ACTIONS.UNMUTE : CHANNEL_ACTIONS.MUTE}
            </button>
            <button
              className="flex items-center gap-1 rounded-lg px-1 py-2 text-left text-s-r hover:bg-base-70 w-full"
              onClick={togglePinChannel}
            >
              <MdOutlinePushPin size={16} className="shrink-0" />
              {isPinned ? CHANNEL_ACTIONS.UNPIN_CHAT : CHANNEL_ACTIONS.PIN_CHAT}
            </button>
          </ConditionalRender>
        </div>
      )}
    </div>
  );
};
